<template>
  <!-- Use ClientOnly for external links -->
  <ClientOnly v-if="isExternalLink">
    <a
      :href="url"
      :target="target"
      data-testid="link-with-animation"
      :class="[$attrs.class]"
      class="button button-arrow flex items-center text-s-900 hover:text-s-700 hover:underline"
      @click="handleClick"
    >
      {{ text }}
      <svg
        viewBox="0 0 6 9"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        class="arrow-icon"
        :class="textColor"
      >
        <g class="arrow-head">
          <path
            d="M1 1C4.5 4 5 4.38484 5 4.5C5 4.61516 4.5 5 1 8"
            stroke="currentColor"
            stroke-width="2"
          />
        </g>
      </svg>
    </a>
    <!-- Fallback for server rendering -->
    <template #fallback>
      <div
        data-testid="link-with-animation"
        :class="[$attrs.class]"
        class="button button-arrow flex items-center text-s-900 hover:text-s-700 hover:underline"
      >
        {{ text }}
        <svg
          viewBox="0 0 6 9"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          class="arrow-icon"
          :class="textColor"
        >
          <g class="arrow-head">
            <path
              d="M1 1C4.5 4 5 4.38484 5 4.5C5 4.61516 4.5 5 1 8"
              stroke="currentColor"
              stroke-width="2"
            />
          </g>
        </svg>
      </div>
    </template>
  </ClientOnly>

  <!-- Use component for non-external links -->
  <component
    :is="typeComponent"
    v-else
    v-bind="getProps"
    external
    data-testid="link-with-animation"
    :class="[$attrs.class]"
    class="button button-arrow flex items-center text-s-900 hover:text-s-700 hover:underline"
    @click="handleClick"
  >
    <p :class="classText" v-html="text" />
    <svg
      viewBox="0 0 6 9"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      class="arrow-icon"
      :class="textColor"
    >
      <g class="arrow-head">
        <path
          d="M1 1C4.5 4 5 4.38484 5 4.5C5 4.61516 4.5 5 1 8"
          stroke="currentColor"
          stroke-width="2"
        />
      </g>
    </svg>
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    text: string
    url?: string
    target?: string
    colorIcon?: string
    classText?: string
  }>(),
  {
    url: '',
    target: '_parent',
    colorIcon: 'p-500',
    classText: '',
  },
)

const emit = defineEmits(['click'])

// Function to determine if a URL is external
const isExternalUrl = (url: string): boolean => {
  if (!url) return false
  return (
    url.startsWith('http') || url.startsWith('//') || props.target === '_blank'
  )
}

// Computed property to determine if the link is external
const isExternalLink = computed(() => {
  return props.target === '_blank' || isExternalUrl(props.url)
})

const typeComponent = computed(() => {
  if (props.url === '') return 'button'

  // For non-external links, use NuxtLink
  return resolveComponent('NuxtLink')
})

const getProps = computed(() => {
  if (props.url === '') return {}

  // Only handle NuxtLink props here, as external links are handled separately
  return { to: props.url, 'no-prefetch': true }
})

const textColor = computed(() => {
  return `text-${props.colorIcon} hover:text-${props.colorIcon}`
})

const handleClick = (event: Event) => {
  event.stopImmediatePropagation()
  emit('click')
}
</script>

<style lang="scss" scoped>
$time-duration: 100ms;
.button {
  transition: all $time-duration ease-in-out;
}
.button-arrow {
  .arrow-icon {
    overflow: visible;
    margin-left: 8px;
    width: 8px;
  }
  .arrow-head {
    transform: translateX(0);
    transition: transform $time-duration ease-in-out;
  }

  &:hover {
    .arrow-head {
      transform: translateX(4px);
    }
  }
}
</style>
